import { inject, Injectable } from '@angular/core';
import { DelayedBehaviorSubject } from '@forgerock/backstage-ui-core';
import type { Observable } from 'rxjs';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { EverythingSearchService } from '../everything-search.service';
import { EverythingSearchResult, EverythingSearchResultContainer } from '../model/everything-search-result';
import type { Section, Tab } from '../model/tabs';
import { SearchParamsAdapterService } from '../search-params/search-params-adapter.service';

@Injectable({
  providedIn: 'root'
})
export class TabService {
  private paramService = inject(SearchParamsAdapterService);
  private searchService = inject(EverythingSearchService);
  tab: Observable<Tab> = this.paramService.params.pipe(map(({ tab }) => tab));
  results = new DelayedBehaviorSubject<TabResults>();
  defaultInitialCount = 4;

  constructor() {
    combineLatest([ this.tab, this.searchService.data$ ])
      .pipe(
        map(([ tab, data ]) => this.getTabResults(tab, data))
      )
      .subscribe({ next: it => this.results.next(it) });
  }

  private getTabResults(tab: Tab, data: EverythingSearchResultContainer): TabResults {
    const { results, total, aggregations } = data;

    // simple case: no grouping
    // return a single section with all the results
    if (!tab.groupBy) {
      return {
        tab,
        sections: [
          {
            results,
            total,
            initialCount: results?.length,
            visibleCount: results?.length
          }
        ]
      };
    }

    // special case: tabs using top-hit aggregations (all, training)
    // construct the sections as per the tab config and mix in the aggregation results
    const aggregationResults = aggregations?.find(it => it.name === tab.groupBy)?.results;
    const sections: TabResultSection[] = tab.sections.map(section => {
      const aggregationResult = aggregationResults?.find(it => it.value === section.id);
      const initialCount = Math.min(section.initialCount ?? this.defaultInitialCount, aggregationResult?.topHits?.length ?? 0);
      return {
        config: section,
        results: aggregationResult?.topHits,
        total: aggregationResult?.total,
        initialCount,
        visibleCount: initialCount
      };
    });

    return { tab, sections };
  }
}

export interface TabResults {
  tab: Tab;
  sections: TabResultSection[];
}

export interface TabResultSection {
  config?: Section;
  results: EverythingSearchResult[];
  total: number;
  /** The number of results that should be shown initially */
  initialCount: number;
  /** This number reflects the state in the UI. Only some results might be currently visible to the user. */
  visibleCount: number;
}
